import { NotificationFragment } from "@graphql/notifications/Notifications.g";
import { NotificationEnum } from "@graphql/generated/graphql.g";
import React from "react";

import { NotificationTypeMappingText } from "@components/Texts";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";
import {
  getNotificationReasonLabel,
  NO_EMPLOYEE_IDENTIFIED,
} from "@utils/notificationReasons";

import {
  DEFAULT_HISTORY_NOTIFICATION_TABLE_COLUMN_SETTINGS,
  DEFAULT_NOTIFICATION_TABLE_COLUMN_ORDER,
  NOTIFICATION_TABLE_COLUMN_LABELS,
  NotificationTableColumns,
} from "./UI/NotificationsTableColumns";

export type NotificationFilterValues = {
  clientId?: string;
  notificationType?: NotificationEnum;
  applicationNameSearch?: string;
  resolvedById?: string;
  resolvedAt_Gte?: string;
  resolvedAt_Lte?: string;
};

export const DEFAULT_NOTIFICATION_FILTER_VALUES: NotificationFilterValues = {
  clientId: undefined,
  notificationType: undefined,
  applicationNameSearch: undefined,
  resolvedById: undefined,
  resolvedAt_Gte: undefined,
  resolvedAt_Lte: undefined,
};

export const NotificationsTableConfig = createTableConfiguration<
  NotificationFragment,
  NotificationTableColumns
>({
  tableId: "notifications-v4",
  columns: {
    [NotificationTableColumns.tenantDisplayName]: {
      id: NotificationTableColumns.tenantDisplayName,
      label:
        NOTIFICATION_TABLE_COLUMN_LABELS[
          NotificationTableColumns.tenantDisplayName
        ],
      fieldPath: "tenantDisplayName",
      sortField: "tenant_display_name",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => renderTenantDisplayName(value),
    },
    [NotificationTableColumns.client]: {
      id: NotificationTableColumns.client,
      label: NOTIFICATION_TABLE_COLUMN_LABELS[NotificationTableColumns.client],
      fieldPath: "client.name",
      sortField: "client__name",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => renderClientName(value),
    },
    [NotificationTableColumns.applicationName]: {
      id: NotificationTableColumns.applicationName,
      label:
        NOTIFICATION_TABLE_COLUMN_LABELS[
          NotificationTableColumns.applicationName
        ],
      fieldPath: "application.resource.productName",
      sortField: "resource__product_name",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: NotificationFragment) =>
        renderApplicationName(value, row, () => {}),
    },
    [NotificationTableColumns.employee]: {
      id: NotificationTableColumns.employee,
      label: NOTIFICATION_TABLE_COLUMN_LABELS[NotificationTableColumns.employee],
      fieldPath: "employee.name",
      sortField: "employee__name",
      type: "custom",
      defaultVisible: true,
      render: (_value: unknown, row: NotificationFragment) =>
        renderEmployee(_value, row),
    },
    [NotificationTableColumns.reason]: {
      id: NotificationTableColumns.reason,
      label: NOTIFICATION_TABLE_COLUMN_LABELS[NotificationTableColumns.reason],
      fieldPath: "reason",
      sortField: "reason",
      type: "custom",
      defaultVisible: true,
      render: (_value: unknown, row: NotificationFragment) =>
        renderReason(_value, row),
    },
    [NotificationTableColumns.notificationType]: {
      id: NotificationTableColumns.notificationType,
      label:
        NOTIFICATION_TABLE_COLUMN_LABELS[
          NotificationTableColumns.notificationType
        ],
      fieldPath: "notificationType",
      sortField: "notification_type",
      type: "custom",
      defaultVisible: false,
      render: (value: unknown, row: NotificationFragment) =>
        renderNotificationType(value, row, () => {}),
    },
    [NotificationTableColumns.createdAt]: {
      id: NotificationTableColumns.createdAt,
      label:
        NOTIFICATION_TABLE_COLUMN_LABELS[NotificationTableColumns.createdAt],
      fieldPath: "createdAt",
      sortField: "created_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => renderCreatedAt(value),
    },
    [NotificationTableColumns.resolvedBy]: {
      id: NotificationTableColumns.resolvedBy,
      label:
        NOTIFICATION_TABLE_COLUMN_LABELS[NotificationTableColumns.resolvedBy],
      fieldPath: "resolvedBy.name",
      sortField: "resolved_by_id",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: NotificationFragment) =>
        renderResolvedBy(value, row),
    },
    [NotificationTableColumns.resolvedAt]: {
      id: NotificationTableColumns.resolvedAt,
      label:
        NOTIFICATION_TABLE_COLUMN_LABELS[NotificationTableColumns.resolvedAt],
      fieldPath: "resolvedAt",
      sortField: "resolved_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => renderResolvedAt(value),
    },
  },
  defaultColumnOrder: DEFAULT_NOTIFICATION_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_HISTORY_NOTIFICATION_TABLE_COLUMN_SETTINGS,
});

export const NotificationsHistoryTableConfig = {
  ...NotificationsTableConfig,
  tableId: "notifications-history-v3",
};

export const getDefaultFilterValues = (): NotificationFilterValues => ({
  ...DEFAULT_NOTIFICATION_FILTER_VALUES,
});

export const areFiltersActive = (
  filterValues: NotificationFilterValues,
  includeHistoryFilters = false,
): boolean => {
  const baseFiltersActive = Boolean(
    filterValues.clientId ||
      filterValues.notificationType ||
      filterValues.applicationNameSearch,
  );

  if (!includeHistoryFilters) {
    return baseFiltersActive;
  }

  return Boolean(
    baseFiltersActive ||
      filterValues.resolvedById ||
      filterValues.resolvedAt_Gte ||
      filterValues.resolvedAt_Lte,
  );
};

export function renderTenantDisplayName(value: unknown): React.ReactNode {
  return <Table.CompanyCell name={String(value || "—")} skipLogo={true} />;
}

export function renderClientName(value: unknown): React.ReactNode {
  return <Table.CompanyCell name={String(value || "")} skipLogo={true} />;
}

export function renderApplicationName(
  _value: unknown,
  row: NotificationFragment,
  onApplicationDashboardClick: (clientId: string, applicationId: string) => void,
): React.ReactNode {
  const mainApp =
    row.application?.resourceGroupApplications?.primaryApplication ||
    row.application;

  return (
    <Clickable
      onClick={() =>
        onApplicationDashboardClick(
          row.client?.id || "",
          row.application?.id || "",
        )
      }
      outline="sm"
      stretch
    >
      <Table.CompanyCell
        name={mainApp?.resource.productName || "Unknown Notification"}
        signedLogoUrl={
          mainApp?.resource.publicResource?.signedLogoUrl || undefined
        }
      />
    </Clickable>
  );
}

export function renderEmployee(
  _value: unknown,
  row: NotificationFragment,
): React.ReactNode {
  if (!row.employee) {
    return (
      <Text size={14} color="textSecondary">
        {NO_EMPLOYEE_IDENTIFIED}
      </Text>
    );
  }

  return (
    <Table.UserCell
      name={row.employee.name}
      email={row.employee.email || undefined}
    />
  );
}

export function renderReason(
  _value: unknown,
  row: NotificationFragment,
): React.ReactNode {
  return (
    <Text size={14}>
      {getNotificationReasonLabel(row.reason, row.notificationType)}
    </Text>
  );
}

export function renderNotificationType(
  value: unknown,
  row: NotificationFragment,
  onNotificationClick: (notificationId: string) => void,
): React.ReactNode {
  if (value === null || value === undefined) {
    return null;
  }

  return (
    <Clickable
      onClick={() => onNotificationClick(row.id)}
      outline="sm"
      stretch
    >
      <NotificationTypeMappingText
        notificationType={value as NotificationEnum}
      />
    </Clickable>
  );
}

export function renderCreatedAt(value: unknown): React.ReactNode {
  if (!value) {
    return <Text size={14}>—</Text>;
  }

  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderResolvedBy(
  value: unknown,
  row: NotificationFragment,
): React.ReactNode {
  const name =
    (value as string | undefined) ||
    row.resolvedBy?.name ||
    row.resolvedBy?.email ||
    "—";

  return <Text size={14}>{name}</Text>;
}

export function renderResolvedAt(value: unknown): React.ReactNode {
  if (!value) {
    return <Text size={14}>—</Text>;
  }

  return <Table.DateCell date={new Date(value as string)} />;
}
